import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { Store } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import { getLoanSummaryRequest } from 'tio-data/requests/user';
import { REQUESTING_APP } from 'tio-common/utils/strategy-finder-0726';

const EMPTY_LOAN_SUMMARY: LoanSummary = { summaryByLoanType: [], errors: [] };
const STRATEGY_FINDER_APP = 'STRATEGY_FINDER';

export default class AuthenticatedRepayingStudentDebtRepaymentStrategyFinderIndexRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;

  async model() {
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id as string;

    const [loanSummaryResult, sf0726Result, strategyFinderResult, personResult] =
      await Promise.allSettled([
        userId
          ? this.store.request(getLoanSummaryRequest(userId))
          : Promise.resolve({ content: EMPTY_LOAN_SUMMARY }),
        this.store.request(
          query<RecommendationModel>('recommendation', {
            'filter[user]': userId as string,
            'filter[requesting-app]': REQUESTING_APP,
            include: 'user',
            sort: '-created-at',
            'page[offset]': 0,
            'page[limit]': 3,
          })
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        this.store.request(
          query<RecommendationModel>('recommendation', {
            'filter[user]': userId as string,
            'filter[requesting-app]': STRATEGY_FINDER_APP,
            include: 'user',
            sort: '-created-at',
            'page[offset]': 0,
            'page[limit]': 5,
          })
        ) as Promise<{ content: { data: RecommendationModel[] } }>,
        this.store.request(
          findRecord<PersonModel>('person', personId, {
            include: 'accounts.financial-institution,accounts.loans,user.answer-profile',
            reload: true,
          })
        ) as Promise<{ content: { data: PersonModel } }>,
      ]);

    const loanSummary =
      loanSummaryResult.status === 'fulfilled'
        ? ((loanSummaryResult.value.content as LoanSummary | undefined) ?? EMPTY_LOAN_SUMMARY)
        : EMPTY_LOAN_SUMMARY;
    const sf0726Recommendations =
      sf0726Result.status === 'fulfilled' ? [...sf0726Result.value.content.data] : [];
    const strategyFinderRecommendations =
      strategyFinderResult.status === 'fulfilled'
        ? [...strategyFinderResult.value.content.data]
        : [];
    const person = personResult.status === 'fulfilled' ? personResult.value.content.data : null;

    // Combined usage history: this pilot's runs alongside the regular Strategy
    // Finder runs, newest first.
    // Compare parsed instants, not strings — serialized offsets flip across
    // DST (-05:00/-06:00) within one list, breaking lexicographic order.
    const history = [...sf0726Recommendations, ...strategyFinderRecommendations]
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
      .slice(0, 10);

    return {
      accounts: person?.accounts ?? [],
      loanSummary,
      person,
      user: person?.user ?? null,
      mostRecentRecommendation: sf0726Recommendations[0] ?? null,
      history,
    };
  }
}
